import React, { useState } from 'react';
import { useMode } from '../contexts/ModeContext';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Free Response Question (FRQ) Component for AP Mode
 * Supports CER (Claim-Evidence-Reasoning) format
 */
const FRQQuestion = ({ question, onAnswer, currentAnswer }) => {
  const { config } = useMode();
  const { t } = useLanguage();
  
  const [answer, setAnswer] = useState(currentAnswer || {
    claim: '',
    evidence: '',
    reasoning: ''
  });

  const [wordCount, setWordCount] = useState({
    claim: 0,
    evidence: 0,
    reasoning: 0
  });

  const handleChange = (field, value) => {
    const newAnswer = { ...answer, [field]: value };
    setAnswer(newAnswer);
    
    // Update word count
    const words = value.trim().split(/\s+/).filter(w => w.length > 0);
    setWordCount({ ...wordCount, [field]: words.length });
    
    // Notify parent
    onAnswer(newAnswer);
  };

  const getWordCountColor = (count, field) => {
    const min = field === 'claim' ? 20 : field === 'evidence' ? 50 : 30;
    if (count === 0) return 'text-gray-400';
    if (count < min) return 'text-yellow-600';
    return 'text-green-600';
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8 space-y-6">
      {/* Question Stem */}
      <div className="border-l-4 border-blue-600 pl-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          Free Response Question
        </h3>
        <p className="text-gray-700 leading-relaxed">
          {question.stem_EN || question.stem_TR}
        </p>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 rounded-lg p-4">
        <p className="text-sm text-blue-900 font-medium mb-2">
          📝 Instructions: Use the CER format
        </p>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• <strong>Claim:</strong> State your answer clearly (20+ words)</li>
          <li>• <strong>Evidence:</strong> Provide data, observations, or facts (50+ words)</li>
          <li>• <strong>Reasoning:</strong> Explain how evidence supports claim (30+ words)</li>
        </ul>
      </div>

      {/* Claim */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          1. Claim (Your Answer)
          <span className={`ml-2 font-normal ${getWordCountColor(wordCount.claim, 'claim')}`}>
            ({wordCount.claim} words)
          </span>
        </label>
        <textarea
          value={answer.claim}
          onChange={(e) => handleChange('claim', e.target.value)}
          placeholder="State your main answer or position clearly and concisely..."
          rows={3}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Evidence */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          2. Evidence (Supporting Data)
          <span className={`ml-2 font-normal ${getWordCountColor(wordCount.evidence, 'evidence')}`}>
            ({wordCount.evidence} words)
          </span>
        </label>
        <textarea
          value={answer.evidence}
          onChange={(e) => handleChange('evidence', e.target.value)}
          placeholder="Provide specific data, observations, or facts from the question or your knowledge. Include numbers, examples, or scientific principles..."
          rows={5}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Reasoning */}
      <div>
        <label className="block text-sm font-bold text-gray-700 mb-2">
          3. Reasoning (Connect Evidence to Claim)
          <span className={`ml-2 font-normal ${getWordCountColor(wordCount.reasoning, 'reasoning')}`}>
            ({wordCount.reasoning} words)
          </span>
        </label>
        <textarea
          value={answer.reasoning}
          onChange={(e) => handleChange('reasoning', e.target.value)}
          placeholder="Explain the scientific principles or logic that connects your evidence to your claim. Why does your evidence support your answer?"
          rows={4}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Submission Status */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <span className="font-semibold text-gray-700">Total Words:</span>
            <span className="ml-2 text-gray-600">
              {wordCount.claim + wordCount.evidence + wordCount.reasoning}
            </span>
          </div>
          <div className="flex space-x-2">
            {wordCount.claim >= 20 && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                ✓ Claim Complete
              </span>
            )}
            {wordCount.evidence >= 50 && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                ✓ Evidence Complete
              </span>
            )}
            {wordCount.reasoning >= 30 && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                ✓ Reasoning Complete
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FRQQuestion;
